/**
 * VistoeraTASK-00567 — Platform Admin tenant-list account lifecycle modals.
 * Opened from the tenants `...` menu; asset counts load on demand only.
 */
import { Alert, Descriptions, Form, Input, Modal, Radio, Select, Space, Spin, Typography, message } from 'antd'
import { useEffect, useState } from 'react'
import {
  platformDeleteTenantAccount,
  platformDeleteTenantTeam,
  platformForceLogoutTenant,
  platformGetTenantAccount,
  platformGetTenantDeleteJob,
  platformPreviewTenantDelete,
  platformResetTenantPassword,
  platformSetTenantAccountActive,
  type TenantAccountDetail,
  type TenantDeleteJobStatus,
  type TenantDeletePreview,
} from '../api'
import type { WorkspaceWithCounts } from '../types'
import { formatDateTime } from './adminShared'
import { platformColors } from './theme'
import { formatPoints } from './tenantListTeamOverview'

const MIN_PASSWORD = 8

type Props = {
  workspace: WorkspaceWithCounts | null
  mode:
    | 'detail'
    | 'reset_password'
    | 'force_logout'
    | 'disable'
    | 'enable'
    | 'delete_account'
    | 'delete_team'
    | null
  onClose: () => void
  onSuccess: () => void
}

export default function TenantAccountActionModals({ workspace, mode, onClose, onSuccess }: Props) {
  const open = !!workspace && !!mode
  const [detail, setDetail] = useState<TenantAccountDetail | null>(null)
  const [preview, setPreview] = useState<TenantDeletePreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pwForm] = Form.useForm<{ new_password: string; new_password_confirm: string; must_change_password: boolean }>()
  const [deleteMode, setDeleteMode] = useState<'keep_assets' | 'with_assets'>('keep_assets')
  const [confirmText, setConfirmText] = useState('')
  const [purgeStatus, setPurgeStatus] = useState<TenantDeleteJobStatus | null>(null)

  useEffect(() => {
    if (!open || !workspace) {
      setDetail(null)
      setPreview(null)
      setConfirmText('')
      setDeleteMode('keep_assets')
      return
    }
    let cancelled = false
    setLoading(true)
    const load = async () => {
      try {
        if (mode === 'detail' || mode === 'reset_password' || mode === 'disable' || mode === 'enable' || mode === 'force_logout') {
          const r = await platformGetTenantAccount(workspace.id)
          if (!cancelled) setDetail(r.data)
        }
        if (mode === 'delete_account') {
          const [d, p] = await Promise.all([
            platformGetTenantAccount(workspace.id),
            platformPreviewTenantDelete(workspace.id, { mode: deleteMode }),
          ])
          if (!cancelled) {
            setDetail(d.data)
            setPreview(p.data)
          }
        }
        if (mode === 'delete_team') {
          const [d, p] = await Promise.all([
            platformGetTenantAccount(workspace.id),
            platformPreviewTenantDelete(workspace.id, { mode: 'entire_team' }),
          ])
          if (!cancelled) {
            setDetail(d.data)
            setPreview(p.data)
          }
        }
      } catch (err) {
        if (!cancelled) message.error((err as Error).message || '加载失败')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, workspace, mode, deleteMode])

  if (!workspace || !mode) return null

  const close = () => {
    if (saving) return
    onClose()
  }

  const run = async (fn: () => Promise<void>, okMsg: string) => {
    if (saving) return
    setSaving(true)
    try {
      await fn()
      message.success(okMsg)
      onSuccess()
      onClose()
    } catch (err) {
      message.error((err as Error).message || '操作失败')
    } finally {
      setSaving(false)
    }
  }

  if (mode === 'detail') {
    return (
      <Modal title="账号详情" open onCancel={close} footer={null} width={640} destroyOnClose>
        {loading || !detail ? (
          <Spin />
        ) : (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="邮箱">{detail.email ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="用户 ID">{detail.user_id ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Workspace ID">{detail.workspace_id}</Descriptions.Item>
            <Descriptions.Item label="Workspace 类型">{detail.workspace_type === 'team' ? '团队' : '个人'}</Descriptions.Item>
            <Descriptions.Item label="所属团队">{detail.workspace_name}</Descriptions.Item>
            <Descriptions.Item label="成员角色">{detail.role ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="账号状态">
              {detail.user_status === 'disabled' ? '已停用' : detail.user_status === 'active' ? '正常' : detail.user_status ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">{detail.created_at ? formatDateTime(detail.created_at) : '—'}</Descriptions.Item>
            <Descriptions.Item label="最近登录">{detail.last_login_at ? formatDateTime(detail.last_login_at) : '—'}</Descriptions.Item>
            <Descriptions.Item label="当前套餐">{detail.plan_key ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="积分余额">{formatPoints(detail.credit_balance)}</Descriptions.Item>
            <Descriptions.Item label="图片资产">{detail.asset_counts.image_assets}</Descriptions.Item>
            <Descriptions.Item label="视频资产">{detail.asset_counts.video_assets}</Descriptions.Item>
            <Descriptions.Item label="音频资产">{detail.asset_counts.audio_assets}</Descriptions.Item>
            <Descriptions.Item label="文本资产">{detail.asset_counts.text_assets}</Descriptions.Item>
            <Descriptions.Item label="Canvas">{detail.asset_counts.canvas_count}</Descriptions.Item>
            <Descriptions.Item label="项目">{detail.asset_counts.project_count}</Descriptions.Item>
            <Descriptions.Item label="执行中任务">{detail.running_jobs}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    )
  }

  if (mode === 'reset_password') {
    return (
      <Modal
        title="重置密码"
        open
        onCancel={close}
        onOk={async () => {
          const values = await pwForm.validateFields()
          await run(
            () =>
              platformResetTenantPassword(workspace.id, {
                new_password: values.new_password,
                new_password_confirm: values.new_password_confirm,
                must_change_password: values.must_change_password !== false,
              }).then(() => undefined),
            '密码已重置，旧 Session 已失效',
          )
        }}
        okText="确认重置"
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        {loading || !detail ? (
          <Spin />
        ) : (
          <>
            <p style={{ marginTop: 0 }}>
              账号：<strong>{detail.email}</strong>
              <br />
              Workspace / Team：{detail.workspace_name}（{detail.workspace_type === 'team' ? '团队' : '个人'}）
            </p>
            <Alert type="info" showIcon style={{ marginBottom: 12 }} message="将使用现有认证哈希机制写入新密码，并立即撤销该账号全部登录 Session。密码不会写入日志或返回给前端。" />
            <Form form={pwForm} layout="vertical" initialValues={{ must_change_password: true }} autoComplete="off">
              <Form.Item
                name="new_password"
                label="新密码"
                extra={`至少 ${MIN_PASSWORD} 位，规则与团队账号密码一致`}
                rules={[
                  { required: true, message: '请填写新密码' },
                  { min: MIN_PASSWORD, message: `密码至少 ${MIN_PASSWORD} 位` },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                name="new_password_confirm"
                label="确认新密码"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: '请再次输入密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) return Promise.resolve()
                      return Promise.reject(new Error('两次输入的密码不一致'))
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item name="must_change_password" label="首次登录要求">
                <Select
                  options={[
                    { value: true, label: '要求下次登录修改密码' },
                    { value: false, label: '可直接使用新密码登录' },
                  ]}
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    )
  }

  if (mode === 'force_logout') {
    return (
      <Modal
        title="强制退出所有设备"
        open
        onCancel={close}
        onOk={() => run(() => platformForceLogoutTenant(workspace.id).then(() => undefined), '已强制退出所有设备')}
        okText="确认强制退出"
        okButtonProps={{ danger: true }}
        confirmLoading={saving}
        destroyOnClose
      >
        <p>
          租户：<strong>{workspace.name}</strong>
          <br />
          账号：{detail?.email ?? workspace.owner_email ?? '—'}
        </p>
        <Alert type="warning" showIcon message="将撤销该账号全部有效 Session / Refresh Token。不会修改密码，也不会删除任何资产。" />
      </Modal>
    )
  }

  if (mode === 'disable' || mode === 'enable') {
    const disabling = mode === 'disable'
    return (
      <Modal
        title={disabling ? '停用账号' : '启用账号'}
        open
        onCancel={close}
        onOk={() =>
          run(
            () => platformSetTenantAccountActive(workspace.id, !disabling).then(() => undefined),
            disabling ? '账号已停用' : '账号已启用',
          )
        }
        okText={disabling ? '确认停用' : '确认启用'}
        okButtonProps={{ danger: disabling }}
        confirmLoading={saving}
        destroyOnClose
      >
        <p>
          账号：<strong>{detail?.email ?? workspace.owner_email ?? '—'}</strong>
          <br />
          Workspace：{workspace.name}
        </p>
        <Alert
          type={disabling ? 'warning' : 'info'}
          showIcon
          message={
            disabling
              ? '停用后禁止登录与业务 API，并撤销现有 Session；账号与资产保留，不删除历史记录。'
              : '启用后恢复登录权限；不会自动恢复已撤销的 Session，也不改变资产归属。'
          }
        />
      </Modal>
    )
  }

  if (mode === 'delete_account') {
    const blockedOwner = !!preview?.is_last_owner_with_peers
    const blockedRefs = deleteMode === 'with_assets' && (preview?.reference_check?.blocked_count ?? 0) > 0
    const blocked = blockedOwner || blockedRefs || preview?.can_proceed === false
    const expected = (preview?.confirmation_token_hint || detail?.email || '').trim()
    const assetTotal =
      deleteMode === 'keep_assets'
        ? 0
        : preview
          ? (preview.reference_check?.safe_to_delete_count ??
            preview.asset_counts.image_assets +
              preview.asset_counts.video_assets +
              preview.asset_counts.audio_assets +
              preview.asset_counts.text_assets)
          : 0
    return (
      <Modal
        title="删除账号"
        open
        onCancel={close}
        onOk={() => {
          if (blockedOwner) {
            message.error('团队仍有其他成员时，不能直接删除唯一 Owner。请先交接 Owner，或改用「删除团队」。')
            return
          }
          if (blockedRefs) {
            message.error(preview?.block_reason || '存在跨成员引用，无法 with_assets 删除')
            return
          }
          if (confirmText.trim().toLowerCase() !== expected.toLowerCase()) {
            message.error('请输入完整账号邮箱以确认删除')
            return
          }
          return run(async () => {
            const r = await platformDeleteTenantAccount(workspace.id, {
              mode: deleteMode,
              confirmation: confirmText.trim(),
            })
            if (r.data.purge_job_id) {
              try {
                const st = await platformGetTenantDeleteJob(workspace.id, r.data.purge_job_id)
                setPurgeStatus(st.data)
              } catch {
                /* progress optional */
              }
            }
          }, '账号已冻结；清理状态见后端删除任务')
        }}
        okText="确认删除"
        okButtonProps={{ danger: true, disabled: blocked || saving }}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        {loading || !preview ? (
          <Spin />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="error"
              showIcon
              message="此操作将删除所选范围内的账号及相关资产，删除后可能无法恢复。"
            />
            {blockedOwner && (
              <Alert
                type="warning"
                showIcon
                message="该账号是团队唯一 Owner，且团队仍有其他成员。请先交接 Owner，或使用「删除团队」。"
              />
            )}
            {blockedRefs && (
              <Alert
                type="warning"
                showIcon
                message={
                  preview.block_reason ||
                  `有 ${preview.reference_check?.blocked_count} 个资产仍被团队内容引用，无法 with_assets 删除。`
                }
              />
            )}
            <div>
              <div>删除对象：{workspace.name}</div>
              <div>邮箱：{preview.email ?? '—'}</div>
              <div>Workspace / Team：{preview.workspace_name}</div>
              <div>Workspace 类型：{preview.workspace_type === 'team' ? '团队' : '个人'}</div>
            </div>
            <div>
              <Typography.Text strong>删除类型</Typography.Text>
              <Radio.Group
                style={{ display: 'block', marginTop: 8 }}
                value={deleteMode}
                onChange={(e) => setDeleteMode(e.target.value)}
                options={[
                  { value: 'keep_assets', label: '仅删除成员账号，保留团队资产（推荐默认）' },
                  { value: 'with_assets', label: '删除成员账号及其无引用资产（有引用则阻止）' },
                ]}
              />
            </div>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="预计删除资产数量">{assetTotal}</Descriptions.Item>
              <Descriptions.Item label="引用冲突资产">
                {preview.reference_check?.blocked_count ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="预计影响成员数量">{preview.affected_members}</Descriptions.Item>
              <Descriptions.Item label="正在执行的任务">{preview.running_jobs}</Descriptions.Item>
              <Descriptions.Item label="是否影响共享资产">
                {deleteMode === 'with_assets' ? '仅无引用的本人资产；有引用则整单阻止' : '否（保留全部团队资产）'}
              </Descriptions.Item>
              <Descriptions.Item label="是否不可恢复">是</Descriptions.Item>
            </Descriptions>
            {purgeStatus?.phase && (
              <Alert
                type="info"
                showIcon
                message={`清理阶段：${purgeStatus.phase} · 已处理 ${purgeStatus.processed_assets ?? 0} / 剩余 ${purgeStatus.remaining_assets ?? 0}`}
              />
            )}
            <Form layout="vertical">
              <Form.Item label={`请输入账号邮箱「${expected}」以确认`} required>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expected}
                  autoComplete="off"
                  style={{ borderColor: platformColors.danger }}
                />
              </Form.Item>
            </Form>
          </Space>
        )}
      </Modal>
    )
  }

  if (mode === 'delete_team') {
    const expected = (preview?.confirmation_token_hint || workspace.name || '').trim()
    const assetTotal = preview
      ? preview.asset_counts.image_assets +
        preview.asset_counts.video_assets +
        preview.asset_counts.audio_assets +
        preview.asset_counts.text_assets +
        preview.asset_counts.canvas_count
      : 0
    return (
      <Modal
        title="删除团队"
        open
        onCancel={close}
        onOk={() => {
          if (confirmText.trim() !== expected) {
            message.error('请输入完整团队名称以确认删除')
            return
          }
          return run(
            () => platformDeleteTenantTeam(workspace.id, { confirmation: confirmText.trim() }).then(() => undefined),
            '团队已冻结并进入清理流程',
          )
        }}
        okText="确认删除团队"
        okButtonProps={{ danger: true }}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        {loading || !preview ? (
          <Spin />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert type="error" showIcon message="此操作将删除所选范围内的账号及相关资产，删除后可能无法恢复。" />
            <div>
              <div>删除对象（团队）：{preview.workspace_name}</div>
              <div>Owner 邮箱：{preview.email ?? '—'}</div>
              <div>删除类型：整个团队 Workspace</div>
            </div>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="预计删除资产数量">{assetTotal}</Descriptions.Item>
              <Descriptions.Item label="预计影响成员数量">{preview.affected_members}</Descriptions.Item>
              <Descriptions.Item label="正在执行的任务">{preview.running_jobs}</Descriptions.Item>
              <Descriptions.Item label="是否影响共享资产">是（团队范围内）</Descriptions.Item>
              <Descriptions.Item label="是否不可恢复">是</Descriptions.Item>
            </Descriptions>
            <Form layout="vertical">
              <Form.Item label={`请输入团队名称「${expected}」以确认`} required>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expected}
                  autoComplete="off"
                  style={{ borderColor: platformColors.danger }}
                />
              </Form.Item>
            </Form>
          </Space>
        )}
      </Modal>
    )
  }

  return null
}
